import React, { useState, useContext, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { AuthContext } from '../context/AuthContext';
import { useToast } from '../hooks/useToast';
import client from '../api/client';
import { Layout, ProtectedRoute } from '../components/layout';
import { Button, Card, CardBody, LoadingSpinner } from '../components/common';
import {
  PhaseIndicator,
  InfluenceDisplay,
  SceneAspects,
  GMPromptEditor,
  PromptDisplay,
  PlayerContributionList,
  RoundCompleteView,
  SetupPhaseEditor,
  SubmissionBreakdown,
  CountdownTimer,
} from '../components/game';
import { CharacterCard, CharacterViewModal, CharacterModal } from '../components/characters';
import { SceneSelectorModal, SceneModal } from '../components/scenes';
import { SubmissionEditor } from '../components/submission';
import { CompositionEditor, WaitingForComposition } from '../components/composition';
import { VotingInterface } from '../components/voting';
import { CollapsibleStoryViewer } from '../components/story';
import { InfluencePhaseView } from '../components/influence';
import { useGameState } from '../hooks/useGames';
import { useCreateRound, useAdvanceRound, useAddRoundAspect } from '../hooks/useRounds';
import { Modal } from '../components/common';
import { useCreateScene } from '../hooks/useScenes';
import { useCreateCharacter, useUpdateCharacter, useDeleteCharacter } from '../hooks/useCharacters';
import { useGameSocket } from '../hooks/useGameSocket';

export const Game = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const auth = useContext(AuthContext);
  const currentUserId = auth?.user?.id;
  const [passageLength, setPassageLength] = useState(2);
  const [viewingCharacter, setViewingCharacter] = useState(null);
  const [editingCharacter, setEditingCharacter] = useState(null);
  const [isCharacterViewOpen, setIsCharacterViewOpen] = useState(false);
  const [isCharacterEditOpen, setIsCharacterEditOpen] = useState(false);
  const [isNotifyModalOpen, setIsNotifyModalOpen] = useState(false);
  const [notifyMessage, setNotifyMessage] = useState('');
  const [notifyType, setNotifyType] = useState('info');
  const [notifySending, setNotifySending] = useState(false);
  const [showNewRoundSetup, setShowNewRoundSetup] = useState(false);
  const [showAdvanceWarning, setShowAdvanceWarning] = useState(false);
  const [showDeadlineWarning, setShowDeadlineWarning] = useState(false);
  const [deadlineInfo, setDeadlineInfo] = useState(null);
  const [selectedSceneId, setSelectedSceneId] = useState(null);
  const [showSceneSelectorModal, setShowSceneSelectorModal] = useState(false);
  const [showCreateSceneModal, setShowCreateSceneModal] = useState(false);

  // Real-time socket connection for this game
  const { isConnected, onlineUserIds } = useGameSocket(id);

  // Fetch combined game state (single API call instead of 9)
  const { data: gameState, isLoading: stateLoading, error: stateError } = useGameState(id);

  // Extract data from combined state
  const game = gameState?.game;
  const round = gameState?.round;
  const scenes = gameState?.scenes || [];
  const characters = gameState?.characters || [];
  const balance = gameState?.myBalance?.balance ?? 0;
  const allBalances = gameState?.balances || [];
  const details = gameState?.details || [];
  const readyStatusData = gameState?.readyStatus;
  const contributionStatus = gameState?.contributionStatus;

  // Update browser tab title with game name
  useEffect(() => {
    if (game?.title) {
      document.title = `${game.title} - Fate After Dice`;
    }
    return () => {
      document.title = 'FAD - Fate After Dice';
    };
  }, [game?.title]);

  // Compute current scene - sceneId is now just an ObjectId string, so look it up
  const currentSceneId = round?.sceneId;
  const lastScene = scenes.length > 0 ? scenes[scenes.length - 1] : null;
  const currentScene = currentSceneId
    ? scenes.find(s => (s.id || s._id) === currentSceneId || s._id === currentSceneId)
    : lastScene;

  // Compute display scene (accounts for user selection when no round active)
  const displayScene = (() => {
    // If there's an active round AND we're not in "new round setup" mode, show the round's scene
    if (round && !showNewRoundSetup) {
      return currentScene;
    }
    // If user selected "new", show null (will create new scene)
    if (selectedSceneId === 'new') {
      return null;
    }
    // If user selected a specific scene, find and show it
    if (selectedSceneId) {
      return scenes.find(s => (s.id || s._id) === selectedSceneId) || currentScene;
    }
    // Default to current/last scene
    return currentScene;
  })();

  // Mutations
  const createRound = useCreateRound(id);
  const createScene = useCreateScene(id);
  const advanceRound = useAdvanceRound(id, round?.id);
  const addRoundAspect = useAddRoundAspect(id, round?.id);
  const createCharacter = useCreateCharacter(id);
  const updateCharacterMutation = useUpdateCharacter(id);
  const deleteCharacter = useDeleteCharacter(id);

  if (stateLoading) {
    return (
      <ProtectedRoute>
        <Layout>
          <div className="flex items-center justify-center min-h-[calc(100vh-64px)]">
            <LoadingSpinner size="lg" />
          </div>
        </Layout>
      </ProtectedRoute>
    );
  }

  if (stateError || !game) {
    return (
      <ProtectedRoute>
        <Layout>
          <div className="max-w-4xl mx-auto px-4 py-8 text-center">
            <p className="text-red-600 mb-4">Game not found or access denied</p>
            <Button onClick={() => navigate('/dashboard')}>Back to Dashboard</Button>
          </div>
        </Layout>
      </ProtectedRoute>
    );
  }

  const isGM = game.myRole === 'gm';

  // Winning detail (for composition and voting phases)
  const winningDetail = details.find(d => d.status === 'active');

  // Character handlers
  const handleViewCharacter = (character) => {
    setViewingCharacter(character);
    setIsCharacterViewOpen(true);
  };

  const handleEditCharacter = (character) => {
    setEditingCharacter(character);
    setIsCharacterEditOpen(true);
  };

  const handleCharacterSave = async (characterData) => {
    try {
      if (editingCharacter) {
        await updateCharacterMutation.mutateAsync({
          characterId: editingCharacter.id || editingCharacter._id,
          ...characterData,
        });
      } else {
        await createCharacter.mutateAsync(characterData);
      }
      setIsCharacterEditOpen(false);
      setEditingCharacter(null);
    } catch (err) {
      console.error('Failed to save character:', err);
    }
  };

  const handleCharacterDelete = async (characterId) => {
    try {
      await deleteCharacter.mutateAsync(characterId);
      setIsCharacterEditOpen(false);
      setEditingCharacter(null);
    } catch (err) {
      console.error('Failed to delete character:', err);
    }
  };

  // Handle starting a new round
  const handleStartRound = async (promptData, passageData = {}) => {
    try {
      // Use selected scene, or current scene, or create a new one
      let sceneId = selectedSceneId;

      // If "new" was selected or no scene exists, create a new one
      if (sceneId === 'new' || (!sceneId && !currentScene)) {
        const sceneResult = await createScene.mutateAsync({
          title: '',
          description: '',
        });
        sceneId = sceneResult.scene.id;
      } else if (!sceneId && currentScene) {
        sceneId = currentScene.id || currentScene._id;
      }

      await createRound.mutateAsync({
        passageLength,
        prompt: promptData,
        sceneId,
        passageTitle: passageData.passageTitle || '',
        passageDescription: passageData.passageDescription || '',
      });

      // Reset state
      setShowNewRoundSetup(false);
      setSelectedSceneId(null);

      // Advance from setup to influence phase
      if (round?.phase === 'setup') {
        await advanceRound.mutateAsync();
      }
    } catch (error) {
      console.error('Failed to start round:', error);
      const errorMessage = error?.response?.data?.error || 'Failed to start round';

      // If a round already exists, refetch game state to show the correct UI
      if (errorMessage.includes('round') && errorMessage.includes('progress')) {
        showToast('A round is already in progress. Refreshing...', 'info');
        queryClient.invalidateQueries({ queryKey: ['gameState', id] });
        setShowNewRoundSetup(false);
      } else {
        showToast(errorMessage, 'error');
      }
    }
  };

  // Handle advancing to next phase
  const handleAdvancePhase = async () => {
    // If phase is ready to advance (deadline passed OR phase-specific conditions met), just advance
    if (isPhaseReadyToAdvance()) {
      await doAdvancePhase();
      return;
    }

    // Otherwise, show appropriate warning
    const currentPhase = round?.phase;
    const deadline = round?.deadlines?.[currentPhase];

    // Check if we're in influence phase and not all players are ready
    if (round?.phase === 'influence' && readyStatusData && !readyStatusData.allReady) {
      setShowAdvanceWarning(true);
      return;
    }

    // Show deadline warning if deadline hasn't passed
    if (deadline) {
      const deadlineDate = new Date(deadline);
      const now = new Date();
      if (deadlineDate > now) {
        const diffMs = deadlineDate - now;
        const diffMins = Math.ceil(diffMs / 60000);
        const hours = Math.floor(diffMins / 60);
        const mins = diffMins % 60;
        let timeRemaining;
        if (hours > 0) {
          timeRemaining = `${hours}h ${mins}m`;
        } else {
          timeRemaining = `${mins} minute${mins !== 1 ? 's' : ''}`;
        }
        setDeadlineInfo({
          phase: currentPhase,
          deadline: deadlineDate,
          timeRemaining,
        });
        setShowDeadlineWarning(true);
        return;
      }
    }

    // Fallback: just advance
    await doAdvancePhase();
  };

  // Actually perform the phase advance
  const doAdvancePhase = async () => {
    try {
      await advanceRound.mutateAsync();
      setShowAdvanceWarning(false);
      setShowDeadlineWarning(false);
      setDeadlineInfo(null);
    } catch (error) {
      console.error('Failed to advance phase:', error);
    }
  };

  // Handle adding an aspect
  const handleAddAspect = async (aspectData) => {
    await addRoundAspect.mutateAsync(aspectData);
  };

  // Handle creating a new scene from the modal
  const handleCreateSceneFromModal = async (sceneData) => {
    try {
      const result = await createScene.mutateAsync(sceneData);
      const newSceneId = result.scene.id || result.scene._id;
      setSelectedSceneId(newSceneId);
      setShowCreateSceneModal(false);
    } catch (error) {
      console.error('Failed to create scene:', error);
    }
  };

  // Handle sending a notification (GM only, for testing)
  const handleSendNotification = async () => {
    if (!notifyMessage.trim()) return;
    setNotifySending(true);
    try {
      await client.post(`/api/games/${id}/notify`, {
        message: notifyMessage,
        type: notifyType,
      });
      setNotifyMessage('');
      setIsNotifyModalOpen(false);
    } catch (err) {
      console.error('Failed to send notification:', err);
    } finally {
      setNotifySending(false);
    }
  };

  // Render setup/no round state (GM needs to start a round)
  const renderNoRoundState = () => (
    <div className="grid md:grid-cols-3 gap-4 sm:gap-6">
      <div className="md:col-span-2">
        {/* Selected Scene Card for GM */}
        {isGM && (
          <Card className="mb-4">
            <CardBody>
              <button
                type="button"
                onClick={() => setShowSceneSelectorModal(true)}
                className="w-full text-left"
              >
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-gray-900">Selected Scene</h3>
                  <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
                <div className="mt-2 flex items-center gap-3">
                  {displayScene ? (
                    <>
                      <span className="flex-shrink-0 w-8 h-8 bg-gray-700 text-white rounded-full flex items-center justify-center text-sm font-bold">
                        {displayScene.number}
                      </span>
                      <div className="min-w-0">
                        <p className="font-medium text-gray-900">
                          {displayScene.title || `Scene ${displayScene.number}`}
                        </p>
                        {displayScene.description && (
                          <p className="text-sm text-gray-500 truncate">{displayScene.description}</p>
                        )}
                        <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                          {displayScene.characterIds?.length > 0 && (
                            <span>{displayScene.characterIds.length} character{displayScene.characterIds.length !== 1 ? 's' : ''}</span>
                          )}
                          {displayScene.aspects?.length > 0 && (
                            <span>{displayScene.aspects.length} aspect{displayScene.aspects.length !== 1 ? 's' : ''}</span>
                          )}
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      <span className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center">
                        <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                      </span>
                      <div>
                        <p className="font-medium text-gray-500">No scene selected</p>
                        <p className="text-sm text-gray-400">Click to select or create a scene</p>
                      </div>
                    </>
                  )}
                </div>
              </button>
            </CardBody>
          </Card>
        )}

        <Card>
          <CardBody>
            <h2 className="text-xl font-bold mb-4">Start a New Round</h2>
            {isGM ? (
              <GMPromptEditor
                gameId={id}
                passageLength={passageLength}
                onPassageLengthChange={setPassageLength}
                onSubmit={handleStartRound}
                loading={createRound.isPending}
                roundNumber={(game.totalRounds || 0) + 1}
                showPassageFields={true}
                initialPassageTitle=""
                initialPassageDescription=""
              />
            ) : (
              <div className="text-center py-8 text-gray-500">
                <p>Waiting for the GM to start a new round...</p>
              </div>
            )}
          </CardBody>
        </Card>
      </div>

      <div className="space-y-4">
        <Card>
          <CardBody>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold">Your Influence</h3>
              <InfluenceDisplay balance={balance} showLabel={false} />
            </div>
          </CardBody>
        </Card>

        {currentScene && (
          <Card>
            <CardBody>
              <SceneAspects
                aspects={currentScene.aspects || []}
                canAdd={false}
                canRemove={isGM}
                isGM={isGM}
              />
            </CardBody>
          </Card>
        )}

        {/* Characters */}
        <Card>
          <CardBody>
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold">Characters</h3>
              {isGM && (
                <button
                  onClick={() => {
                    setEditingCharacter(null);
                    setIsCharacterEditOpen(true);
                  }}
                  className="text-xs text-primary-600 hover:text-primary-700"
                >
                  + Add
                </button>
              )}
            </div>
            {characters.length === 0 ? (
              <p className="text-sm text-gray-500 italic">No characters yet.</p>
            ) : (
              <div className="space-y-2">
                {characters.map((character) => (
                  <CharacterCard
                    key={character.id || character._id}
                    character={character}
                    onClick={() => handleViewCharacter(character)}
                    compact={true}
                  />
                ))}
              </div>
            )}
          </CardBody>
        </Card>
      </div>
    </div>
  );

  // Get the phase deadline for display
  const getPhaseDeadline = () => {
    if (!round?.deadlines) return null;
    switch (round.phase) {
      case 'influence': return round.deadlines.influence;
      case 'submission': return round.deadlines.submission;
      case 'composition': return round.deadlines.composition;
      case 'voting': return round.deadlines.voting;
      default: return null;
    }
  };

  // Check if phase is ready to advance (deadline passed OR all work done)
  const isPhaseReadyToAdvance = () => {
    if (!round) return false;

    // Check if deadline has passed
    const deadline = getPhaseDeadline();
    if (deadline && new Date(deadline) <= new Date()) {
      return true;
    }

    // Check phase-specific completion
    switch (round.phase) {
      case 'influence':
        // All players marked ready
        return readyStatusData?.allReady === true;
      case 'submission':
        // All players have submitted
        return contributionStatus?.summary?.submitted === contributionStatus?.summary?.total;
      case 'composition':
        // GM has finished (we don't have a specific flag, so just use deadline)
        return false;
      case 'voting':
        // All votes in (would need voting data - for now just deadline)
        return false;
      default:
        return false;
    }
  };

  // Get scene display name
  const getSceneDisplayName = () => {
    if (!displayScene) return 'No Scene';
    if (displayScene.title?.trim()) return displayScene.title;
    return `Scene ${displayScene.number}`;
  };

  // Render active round state
  const renderActiveRound = () => (
    <div className="space-y-4">
      {/* Round Overview and Scene Cards - side by side on desktop */}
      <div className="grid lg:grid-cols-2 gap-4">
        {/* Round Overview Card */}
        <Card>
          <CardBody>
            {/* Header row: Scene/Round + Notify + Time */}
            <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
              <h2 className="text-lg font-bold text-gray-900">
                {getSceneDisplayName()} — Round {round.number}
              </h2>
              <div className="flex items-center gap-2">
                {isGM && round.phase !== 'complete' && round.phase !== 'voting' && (
                  <button
                    onClick={() => setIsNotifyModalOpen(true)}
                    className="p-1.5 text-gray-500 hover:text-primary-600 hover:bg-gray-100 rounded-lg transition-colors"
                    title="Notify Players"
                  >
                    <span className="text-lg">📢</span>
                  </button>
                )}
                {getPhaseDeadline() && (
                  <CountdownTimer
                    deadline={getPhaseDeadline()}
                    label="Time"
                  />
                )}
              </div>
            </div>

            {/* Advance button - own line, blue when ready */}
            {isGM && round.phase !== 'complete' && round.phase !== 'voting' && (
              <div className="mb-3">
                <Button
                  variant={isPhaseReadyToAdvance() ? 'primary' : 'secondary'}
                  className="w-full"
                  onClick={handleAdvancePhase}
                  loading={advanceRound.isPending}
                >
                  Advance
                </Button>
              </div>
            )}

            {/* Phase Indicator */}
            <div className="border-t border-b py-3 mb-4">
              <PhaseIndicator currentPhase={round.phase} />
            </div>

            {/* Players Grid with Influence */}
            <div className="mb-4">
              <h3 className="text-sm font-medium text-gray-700 mb-2">Players</h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {/* All players from balances, with GM badge if applicable */}
                {allBalances.map((balanceItem) => {
                  const isUserGM = balanceItem.userId === game.gmUserId;
                  return (
                    <div
                      key={balanceItem.userId}
                      className={`flex items-center justify-between rounded-lg px-3 py-2 ${isUserGM ? 'bg-primary-50' : 'bg-gray-50'}`}
                    >
                      <div className="flex items-center gap-2 min-w-0 flex-1">
                        <span
                          className={`w-2 h-2 rounded-full flex-shrink-0 ${onlineUserIds.includes(balanceItem.userId) ? 'bg-green-500' : 'bg-gray-300'}`}
                        />
                        <span className="text-sm text-gray-900 truncate">
                          {balanceItem.displayName || 'Player'}
                        </span>
                        {isUserGM && (
                          <span className="text-xs bg-primary-600 text-white px-1.5 py-0.5 rounded flex-shrink-0">GM</span>
                        )}
                      </div>
                      <span className="text-sm font-medium text-amber-600 flex-shrink-0 ml-2">
                        {balanceItem.balance}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Length and Pot - prominent display */}
            <div className="flex items-center justify-center gap-6 bg-gray-50 rounded-lg py-3">
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-900">{round.passageLength}</div>
                <div className="text-xs text-gray-500 uppercase tracking-wide">Length</div>
              </div>
              <div className="w-px h-10 bg-gray-300" />
              <div className="text-center">
                <div className="text-2xl font-bold text-amber-600">{round.pot}</div>
                <div className="text-xs text-gray-500 uppercase tracking-wide">Pot</div>
              </div>
            </div>
          </CardBody>
        </Card>

        {/* Scene Card */}
        {currentScene && (
          <Card>
            <CardBody>
              <h3 className="font-semibold text-gray-900 mb-3">Scene</h3>

              {/* Scene Aspects */}
              <SceneAspects
                aspects={currentScene.aspects || []}
                canAdd={['influence', 'submission'].includes(round.phase)}
                canRemove={isGM}
                onAdd={handleAddAspect}
                isGM={isGM}
                balance={balance}
              />

              {/* Characters in Scene */}
              {characters.length > 0 && (
                <div className="mt-4 pt-4 border-t">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-sm font-medium text-gray-700">Characters</h4>
                    {isGM && (
                      <button
                        onClick={() => {
                          setEditingCharacter(null);
                          setIsCharacterEditOpen(true);
                        }}
                        className="text-xs text-primary-600 hover:text-primary-700"
                      >
                        + Add
                      </button>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    {characters.map((character) => (
                      <CharacterCard
                        key={character.id || character._id}
                        character={character}
                        onClick={() => handleViewCharacter(character)}
                        compact={true}
                      />
                    ))}
                  </div>
                </div>
              )}
            </CardBody>
          </Card>
        )}
      </div>

      {/* Phase Content - full width */}
      <div className="space-y-4">
        {/* GM's Prompt (hidden during setup, hidden during voting since VotingInterface displays it) */}
        {round.phase !== 'setup' && round.phase !== 'voting' && (
          <Card>
            <CardBody>
              <PromptDisplay prompt={round.prompt} />
            </CardBody>
          </Card>
        )}

        {/* Winning Detail - shown during composition phase (voting phase handled by VotingInterface) */}
        {winningDetail && round.phase === 'composition' && (
          <div className="bg-amber-50 border-2 border-amber-400 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-8 h-8 bg-amber-400 rounded-full flex items-center justify-center">
                <span className="text-white font-bold text-sm">!</span>
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-amber-800 mb-1">
                  Winning Detail
                </h3>
                <p className="text-amber-900 font-medium">
                  "{winningDetail.content}"
                </p>
                <p className="text-sm text-amber-700 mt-1">
                  By {winningDetail.authorUserId?.displayName || 'Unknown'} · {winningDetail.totalBid || 1} bid{(winningDetail.totalBid || 1) !== 1 ? 's' : ''}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Player Contribution Status - shown during submission phase only */}
        {round.phase === 'submission' && (
          <Card>
            <CardBody className="py-3">
              <PlayerContributionList
                gameId={id}
                roundId={round.id}
                showDuringPhases={['submission']}
                onlineUserIds={onlineUserIds}
              />
            </CardBody>
          </Card>
        )}

        {/* Phase-specific content */}
        {round.phase === 'setup' && isGM && (
          <Card>
            <CardBody>
              <SetupPhaseEditor
                gameId={id}
                round={round}
                balance={balance}
              />
            </CardBody>
          </Card>
        )}

        {round.phase === 'setup' && !isGM && (
          <Card>
            <CardBody>
              <div className="text-center py-6 text-gray-500">
                <div className="mb-3">
                  <svg
                    className="w-10 h-10 mx-auto text-gray-300"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-700 mb-1">
                  Round Setup in Progress
                </h3>
                <p className="text-sm">The GM is setting up the round...</p>
              </div>

              {/* GM Prompt Preview (if available) */}
              {round.prompt && (
                <div className="mt-4 pt-4 border-t">
                  <div className="bg-gray-50 rounded-lg p-3">
                    <PromptDisplay prompt={round.prompt} />
                  </div>
                </div>
              )}
            </CardBody>
          </Card>
        )}

        {round.phase === 'influence' && (
          <InfluencePhaseView
            gameId={id}
            roundId={round.id}
            isGM={isGM}
            balance={balance}
            currentUserId={currentUserId}
          />
        )}

        {round.phase === 'submission' && !isGM && (
          <Card>
            <CardBody>
              <h2 className="text-lg font-bold mb-4">Your Submission</h2>
              <SubmissionEditor
                gameId={id}
                roundId={round.id}
                aspects={currentScene?.aspects || []}
                balance={balance}
              />
            </CardBody>
          </Card>
        )}

        {round.phase === 'submission' && isGM && (
          <SubmissionBreakdown
            gameId={id}
            roundId={round.id}
          />
        )}

        {round.phase === 'composition' && isGM && (
          <CompositionEditor
            gameId={id}
            roundId={round.id}
          />
        )}

        {round.phase === 'composition' && !isGM && (
          <WaitingForComposition />
        )}

        {round.phase === 'voting' && (
          <VotingInterface
            gameId={id}
            roundId={round.id}
            isGM={isGM}
            prompt={round.prompt}
            winningDetail={winningDetail}
          />
        )}
      </div>
    </div>
  );

  return (
    <ProtectedRoute>
      <Layout>
        <div className="max-w-6xl mx-auto px-4 py-6">
          {/* Header - Just game name */}
          <div className="mb-6 flex items-center justify-between">
            <Link
              to="/dashboard"
              className="flex items-center gap-1 text-gray-500 hover:text-gray-700 flex-shrink-0"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              <span className="text-sm hidden sm:inline">Dashboard</span>
            </Link>
            <h1 className="text-lg sm:text-xl font-bold truncate text-center flex-1 px-4">{game.title}</h1>
            <div className="flex items-center gap-2 flex-shrink-0">
              <span
                className={`w-2 h-2 rounded-full ${isConnected ? 'bg-green-500' : 'bg-red-500'}`}
                title={isConnected ? 'Connected' : 'Disconnected'}
              />
              {isGM && (
                <span className="px-2 py-1 text-xs font-medium rounded-full bg-primary-100 text-primary-800">
                  GM
                </span>
              )}
            </div>
          </div>

          {/* Main content */}
          {!round || showNewRoundSetup
            ? renderNoRoundState()
            : round.phase === 'complete'
              ? <RoundCompleteView
                  gameId={id}
                  roundId={round.id}
                  round={round}
                  isGM={isGM}
                  onStartNewRound={() => setShowNewRoundSetup(true)}
                />
              : renderActiveRound()
          }

          {/* Story Viewer */}
          <CollapsibleStoryViewer gameId={id} />

          {/* Character View Modal */}
          <CharacterViewModal
            isOpen={isCharacterViewOpen}
            onClose={() => {
              setIsCharacterViewOpen(false);
              setViewingCharacter(null);
            }}
            character={viewingCharacter}
            onEdit={handleEditCharacter}
            canEdit={isGM}
          />

          {/* Character Edit Modal */}
          <CharacterModal
            isOpen={isCharacterEditOpen}
            onClose={() => {
              setIsCharacterEditOpen(false);
              setEditingCharacter(null);
            }}
            gameId={id}
            character={editingCharacter}
            onSave={handleCharacterSave}
            onDelete={isGM ? handleCharacterDelete : undefined}
            isSaving={createCharacter.isPending || updateCharacterMutation.isPending}
          />

          {/* Notify Players Modal (GM only, for testing) */}
          {isNotifyModalOpen && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
                <h3 className="text-lg font-semibold mb-4">Notify Players</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Message
                    </label>
                    <input
                      type="text"
                      value={notifyMessage}
                      onChange={(e) => setNotifyMessage(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                      placeholder="Enter notification message..."
                      maxLength={200}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Type
                    </label>
                    <select
                      value={notifyType}
                      onChange={(e) => setNotifyType(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    >
                      <option value="info">Info</option>
                      <option value="success">Success</option>
                      <option value="error">Error</option>
                      <option value="award">Award</option>
                    </select>
                  </div>
                  <div className="flex gap-2 justify-end">
                    <Button
                      variant="outline"
                      onClick={() => {
                        setIsNotifyModalOpen(false);
                        setNotifyMessage('');
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={handleSendNotification}
                      loading={notifySending}
                      disabled={!notifyMessage.trim()}
                    >
                      Send
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Advance Phase Warning Modal (GM override) */}
          <Modal
            isOpen={showAdvanceWarning}
            onClose={() => setShowAdvanceWarning(false)}
            title="Not All Players Ready"
            size="sm"
          >
            <div className="space-y-4">
              <p className="text-gray-700">
                Not all players have marked themselves as ready for this phase.
              </p>

              {readyStatusData && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <p className="text-sm text-amber-800 font-medium mb-2">
                    {readyStatusData.readyCount} of {readyStatusData.totalPlayers} players ready
                  </p>
                  <div className="space-y-1">
                    {readyStatusData.players?.filter(p => !p.isReady).map(player => (
                      <p key={player.id} className="text-sm text-amber-700">
                        • {player.displayName} is not ready
                      </p>
                    ))}
                  </div>
                </div>
              )}

              <p className="text-sm text-gray-500">
                As GM, you can override this and advance anyway.
              </p>

              <div className="flex gap-3 justify-end pt-2">
                <Button
                  variant="secondary"
                  onClick={() => setShowAdvanceWarning(false)}
                >
                  Wait for Players
                </Button>
                <Button
                  onClick={doAdvancePhase}
                  loading={advanceRound.isPending}
                >
                  Advance Anyway
                </Button>
              </div>
            </div>
          </Modal>

          {/* Deadline Warning Modal (GM override) */}
          <Modal
            isOpen={showDeadlineWarning}
            onClose={() => {
              setShowDeadlineWarning(false);
              setDeadlineInfo(null);
            }}
            title="Deadline Not Reached"
            size="sm"
          >
            <div className="space-y-4">
              <p className="text-gray-700">
                The deadline for the <span className="font-medium capitalize">{deadlineInfo?.phase}</span> phase has not been reached yet.
              </p>

              {deadlineInfo && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <p className="text-sm text-blue-800 font-medium mb-1">
                    Time Remaining: {deadlineInfo.timeRemaining}
                  </p>
                  <p className="text-xs text-blue-600">
                    Deadline: {deadlineInfo.deadline.toLocaleString()}
                  </p>
                </div>
              )}

              <p className="text-sm text-gray-500">
                Players may still be working on their contributions. As GM, you can override this and advance anyway.
              </p>

              <div className="flex gap-3 justify-end pt-2">
                <Button
                  variant="secondary"
                  onClick={() => {
                    setShowDeadlineWarning(false);
                    setDeadlineInfo(null);
                  }}
                >
                  Wait for Deadline
                </Button>
                <Button
                  onClick={doAdvancePhase}
                  loading={advanceRound.isPending}
                >
                  Advance Anyway
                </Button>
              </div>
            </div>
          </Modal>

          {/* Scene Selector Modal */}
          <SceneSelectorModal
            isOpen={showSceneSelectorModal}
            onClose={() => setShowSceneSelectorModal(false)}
            scenes={scenes}
            selectedSceneId={selectedSceneId}
            currentSceneId={currentScene?.id || currentScene?._id}
            onSelectScene={(sceneId) => setSelectedSceneId(sceneId)}
            onCreateNew={() => {
              setShowSceneSelectorModal(false);
              setShowCreateSceneModal(true);
            }}
            isCreating={createScene.isPending}
          />

          {/* Create Scene Modal */}
          <SceneModal
            isOpen={showCreateSceneModal}
            onClose={() => setShowCreateSceneModal(false)}
            scene={null}
            characters={characters}
            onSave={handleCreateSceneFromModal}
            isSaving={createScene.isPending}
          />
        </div>
      </Layout>
    </ProtectedRoute>
  );
};
